/**
 * Construtores de breadcrumbs e Schema.org BreadcrumbList.
 */

import { slugify, buildPropertyPath } from './urlHelpers';

export function buildPropertyBreadcrumbs(property, baseUrl) {
  const items = [
    { name: 'Início', path: '/' },
    { name: 'Imóveis', path: '/propriedades' },
  ];

  items.push({
    name: property?.title || 'Imóvel',
    path: property?.id ? buildPropertyPath(property) : null,
    current: true,
  });

  return items;
}

export function buildListingBreadcrumbs(filters = {}, options = {}) {
  const { cityName, neighborhoodName, typeName } = options;
  const items = [
    { name: 'Início', path: '/' },
    { name: 'Imóveis', path: '/propriedades' },
  ];

  if (cityName) {
    items.push({
      name: cityName,
      path: `/propriedades/${slugify(cityName)}`,
      current: !neighborhoodName && !typeName,
    });
  }

  if (cityName && neighborhoodName) {
    items.push({
      name: neighborhoodName,
      path: `/propriedades/${slugify(cityName)}/${slugify(neighborhoodName)}`,
      current: !typeName,
    });
  }

  if (typeName) {
    if (cityName) {
      const base = neighborhoodName
        ? `/propriedades/${slugify(cityName)}/${slugify(neighborhoodName)}`
        : `/propriedades/${slugify(cityName)}`;
      items.push({
        name: typeName,
        path: `${base}/${slugify(typeName)}`,
        current: true,
      });
    } else {
      items.push({
        name: typeName,
        path: `/propriedades/tipo/${slugify(typeName)}`,
        current: true,
      });
    }
  }

  return items;
}

export function buildBreadcrumbSchema(items, baseUrl) {
  const base = (baseUrl || '').replace(/\/$/, '');
  const list = (items || []).filter((item) => item.name);

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: list.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      ...(item.path && !item.current
        ? { item: `${base}${item.path.startsWith('/') ? item.path : `/${item.path}`}` }
        : {}),
    })),
  };
}

export function buildListingBreadcrumbSchema(filters, options, baseUrl) {
  const items = buildListingBreadcrumbs(filters, options);
  return buildBreadcrumbSchema(items, baseUrl);
}

export function buildPropertyBreadcrumbSchema(property, baseUrl) {
  const items = buildPropertyBreadcrumbs(property, baseUrl);
  return buildBreadcrumbSchema(items, baseUrl);
}

export function buildCondominiumBreadcrumbs(condo) {
  const items = [
    { name: 'Início', path: '/' },
    { name: 'Condomínios', path: '/condominios' },
  ];

  if (condo?.city) {
    items.push({
      name: condo.city,
      path: '/condominios',
    });
  }

  items.push({
    name: condo?.title || 'Condomínio',
    path: condo?.id ? `/condominios/${condo.id}` : null,
    current: true,
  });

  return items;
}

export function buildCondominiumBreadcrumbSchema(condo, baseUrl) {
  const items = buildCondominiumBreadcrumbs(condo);
  return buildBreadcrumbSchema(items, baseUrl);
}

export function buildBlogListingBreadcrumbs(categoryName = null) {
  const items = [
    { name: 'Início', path: '/' },
    { name: 'Blog', path: '/blog', current: !categoryName },
  ];
  if (categoryName) {
    items.push({
      name: categoryName,
      path: `/blog?category=${encodeURIComponent(categoryName)}`,
      current: true,
    });
  }
  return items;
}

export function buildBlogPostBreadcrumbs(post) {
  const items = [
    { name: 'Início', path: '/' },
    { name: 'Blog', path: '/blog' },
  ];
  if (post?.category) {
    items.push({
      name: post.category,
      path: `/blog?category=${encodeURIComponent(post.category)}`,
    });
  }
  items.push({
    name: post?.title || 'Artigo',
    path: post?.slug ? `/blog/${post.slug}` : null,
    current: true,
  });
  return items;
}

export function buildBlogPostBreadcrumbSchema(post, baseUrl) {
  return buildBreadcrumbSchema(buildBlogPostBreadcrumbs(post), baseUrl);
}

export function buildBlogListingBreadcrumbSchema(categoryName, baseUrl) {
  return buildBreadcrumbSchema(buildBlogListingBreadcrumbs(categoryName), baseUrl);
}
